import { createElement, Fragment, memo, useRef } from 'react';
import { PageControlDotSelected, User, UserGroup } from '@sas/react-svg';
import { BaseItemProps } from './types';
import { renderInlineCssVars, useDesignTokens } from '../../../../theme';
import { isNullish } from '@sas/nova-core/typeguards';
import { postfixTestId } from '../../../../util/testId';
import { Avatar } from '../../../Avatar';
import { Icon } from '../../../Icon';
import { GetIconA11yInfo, useIconA11yInfo } from '../../../Icon/useIconA11yInfo';
import { Flow } from '../../../Layouts/Flow';
import { Text } from '../../../Text';

import cx from 'classnames';
import '../Item.css';
import cn from '../Item.css.json';

/**
 * @internal
 */
export const BaseItem = memo((props: BaseItemProps) => {
  const refDiv = useRef<HTMLDivElement | null>(null);
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(
    {
      fg: props.tokens?.fg,
      'font-family': props.tokens?.fontFamily,
      'font-size': props.tokens?.fontSize,
      'font-weight': props.tokens?.fontWeight,
      'font-style': props.tokens?.fontStyle,
      'text-decoration': props.tokens?.textDecoration,
      gap: props.info?.concatInfo ? '' : dt.spacing.inline.s,
    },
    'list-item'
  );
  const getIconA11yInfo = useIconA11yInfo();

  const itemContent = (
    <div
      style={{ ...tokens, ...props.style }}
      className={cx(
        cn.item,
        props.className,
        props.disabled && cn.disabled,
        props.unread === false && cn.read,
        props.unread === true && cn.unread
      )}
      ref={refDiv}
      data-testid={props.testId}
      title={props.tooltip}
      dir={props.dir}
    >
      {props.unread === true ? (
        <Icon
          icon={PageControlDotSelected}
          tokens={() => ({ 'default-fg': 'var(--list-item-unread-bg)' })}
          className={cn.dot}
        />
      ) : null}
      <Flow spacing="s" className={cn.contentContainer}>
        {props.icon ? ItemIcon(props) : ItemAvatar(props, getIconA11yInfo)}
        {props.description ? DoubleLines(props) : SingleLine(props)}
      </Flow>
      {props.description && props.info && !props.info.concatInfo ? InfoText(props) : undefined}
      {Counter(props)}
    </div>
  );

  return itemContent;
});

const SingleLine = (props: BaseItemProps) => {
  return (
    <div className={cn.singleLine}>
      {ItemText(props)}
      {InfoText(props)}
    </div>
  );
};

const DoubleLines = (props: BaseItemProps) => {
  return (
    <div className={cn.doubleLines}>
      {ItemText(props)}
      {SubSection(props)}
    </div>
  );
};

const SubSection = (props: BaseItemProps) => {
  return (
    <span className={cn.subSection}>
      <Text
        testId={postfixTestId(props.testId, 'description')}
        className={cn.itemDescriptionText}
        disabled={props.disabled}
        highlightableConfig={{
          highlight: !props.highlight?.excludeDescription ? props.highlight?.text : undefined,
        }}
        wrapText={false}
      >
        {props.description ?? ''}
      </Text>
      {props.info?.concatInfo ? InfoText(props) : undefined}
    </span>
  );
};

const ItemText = (props: BaseItemProps) => {
  const wrap = !isNullish(props.titleMaxLines);
  return (
    <Text
      id={props.id}
      className={cx(
        cn.itemText,
        wrap && cn.wrap,
        props.description && [cn.bold, cn.description],
        props.mixedLines && [cn.bold]
      )}
      testId={postfixTestId(props.testId, 'title')}
      disabled={props.disabled}
      highlightableConfig={{
        highlight: !props.highlight?.excludeTitle ? props.highlight?.text : undefined,
      }}
      wrapText={wrap}
      maxLines={props.titleMaxLines}
      dir={props.dir}
    >
      {props.title ?? ''}
    </Text>
  );
};

const InfoText = (props: BaseItemProps) => {
  if (!props.info) {
    return null;
  }

  const hyphen = props.info.concatInfo ? (
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    <Text className={cn.itemTextSeparator} disabled={props.disabled} dir={props.dir}>
      -
    </Text>
  ) : null;
  const secondaryText = (
    <Text
      className={cx(cn.itemInfoText, props.info.concatInfo && cn.concat)}
      testId={postfixTestId(props.testId, 'info')}
      disabled={props.disabled}
      highlightableConfig={{
        highlight: !props.highlight?.excludeInfo ? props.highlight?.text : undefined,
      }}
      wrapText={false}
      dir={props.dir}
    >
      {props.info.infoText ?? ''}
    </Text>
  );

  return props.info.concatInfo ? (
    <Fragment>
      {hyphen}
      {secondaryText}
    </Fragment>
  ) : (
    <Fragment>{secondaryText}</Fragment>
  );
};

const Counter = (props: BaseItemProps) => {
  if (isNullish(props.counter)) {
    return null;
  }
  return (
    <Text
      data-testid={postfixTestId(props.testId, 'counter')}
      className={cn.counter}
      disabled={props.disabled}
      highlightableConfig={{
        highlight: !props.highlight?.excludeCounter ? props.highlight?.text : undefined,
      }}
      wrapText={false}
    >
      {`(${props.counter})`}
    </Text>
  );
};

const ItemIcon = (props: BaseItemProps) => {
  if (isNullish(props.icon)) {
    return null;
  }
  return (
    <Icon
      {...props.icon}
      className={cx(cn.itemIcon, props.icon.className)}
      tokens={
        props.icon.tokens ? props.icon.tokens : () => ({ 'default-fg': 'var(--list-item-fg)' })
      }
      disabled={props.disabled}
    />
  );
};

const ItemAvatar = (props: BaseItemProps, getIconA11yInfo: GetIconA11yInfo) => {
  const { avatar } = props;
  if (isNullish(avatar)) {
    return null;
  }
  return (
    <Avatar
      {...avatar}
      size="small"
      ariaLabel={avatar.ariaLabel ?? ''}
      tooltip={
        avatar.tooltip ??
        getIconA11yInfo({
          icon: avatar.type === 'user' ? User : UserGroup,
          // the following testId is required for testing to get some tooltip
          // to work with (ends up SASDesign-Icon-gui-icu.User.icon.label or
          // SASDesign-Icon-gui-icu.UserGroup.icon.label)
          testId: postfixTestId(props.testId, 'icon'),
        }).tooltip
      }
      className={cx(cn.itemAvatar, avatar.className)}
    />
  );
};
